import { Card, Flex, Tag, Typography } from 'antd'
import { useTranslation } from 'react-i18next'
import { Link } from 'react-router-dom'

import { SoonTag } from 'src/components'
import { paths } from 'src/router'

import { useStyles } from './styles'

const { Text } = Typography

type SampleApp = {
  id: string
  name: string
  category: string
  initial: string
  color: string
  users: number
  roles: number
  health: 'healthy' | 'attention' | 'degraded'
  activeSessions: number
  errorRate: number
  p95: number
  spark: number[]
}

// Sample app health — illustrative until the application telemetry endpoint ships.
const SAMPLE_APPS: SampleApp[] = [
  {
    id: 'crm',
    name: 'Skalx CRM',
    category: 'Sales',
    initial: 'C',
    color: '#0F766E',
    users: 28,
    roles: 6,
    health: 'healthy',
    activeSessions: 1248,
    errorRate: 0.2,
    p95: 184,
    spark: [40, 48, 55, 62, 58, 30, 18, 55, 68, 72, 78, 82, 76, 42, 28, 86]
  },
  {
    id: 'hrm-ats',
    name: 'HRM ATS',
    category: 'People',
    initial: 'H',
    color: '#7C3AED',
    users: 12,
    roles: 4,
    health: 'attention',
    activeSessions: 326,
    errorRate: 0.8,
    p95: 312,
    spark: [20, 22, 21, 24, 28, 12, 8, 28, 32, 30, 28, 30, 30, 18, 10, 30]
  },
  {
    id: 'hris',
    name: 'HRIS',
    category: 'People',
    initial: 'H',
    color: '#2563EB',
    users: 18,
    roles: 5,
    health: 'healthy',
    activeSessions: 278,
    errorRate: 0.1,
    p95: 142,
    spark: [10, 12, 14, 18, 20, 30, 42, 38, 34, 28, 22, 18, 14, 12, 10, 16]
  }
]

const HEALTH_COLORS: Record<SampleApp['health'], string> = {
  healthy: 'success',
  attention: 'warning',
  degraded: 'error'
}

const Sparkline = ({ data, color }: { data: number[]; color: string }) => {
  const width = 100
  const height = 32
  const min = Math.min(...data)
  const max = Math.max(...data)
  const range = max - min || 1
  const step = width / (data.length - 1 || 1)
  const points = data.map((v, i) => {
    const x = i * step
    const y = height - ((v - min) / range) * (height - 4) - 2

    return [x, y] as const
  })
  const path = points
    .map((p, i) => `${i === 0 ? 'M' : 'L'}${p[0]},${p[1]}`)
    .join(' ')
  const area = `${path} L${width},${height} L0,${height} Z`
  const last = points[points.length - 1]

  return (
    <svg
      width={width}
      height={height}
      viewBox={`0 0 ${width} ${height}`}
      style={{ display: 'block' }}
      aria-hidden
    >
      <path d={area} fill={color} opacity={0.1} />
      <path
        d={path}
        fill="none"
        stroke={color}
        strokeWidth={1.5}
        strokeLinecap="round"
        strokeLinejoin="round"
      />
      <circle cx={last[0]} cy={last[1]} r={2.5} fill={color} />
    </svg>
  )
}

type Props = {
  workspaceIdParam: string
}

export const OverviewAppsHealth = ({ workspaceIdParam }: Props) => {
  const { styles } = useStyles()
  const { t } = useTranslation()

  return (
    <Card variant="outlined" className={styles.panelCard}>
      <Flex className={styles.panelHeader}>
        <Flex vertical gap={2}>
          <Flex align="center" gap={6}>
            <Text className={styles.panelTitle}>
              {t('workspaceOverview.appsHealth.title')}
            </Text>
            <SoonTag />
          </Flex>
          <Text className={styles.panelSubtitle}>
            {t('workspaceOverview.appsHealth.subtitle')}
          </Text>
        </Flex>
        <Link
          to={paths.workspaces.apps.root(workspaceIdParam)}
          style={{ fontSize: 12 }}
        >
          {t('workspaceOverview.appsHealth.manage')}
        </Link>
      </Flex>

      <Flex vertical>
        {SAMPLE_APPS.map((app) => (
          <Flex key={app.id} className={styles.appHealthRow}>
            <Flex align="center" gap={11} style={{ flex: 1, minWidth: 0 }}>
              <div
                className={styles.appIcon}
                style={{
                  background: `linear-gradient(135deg, ${app.color} 0%, ${app.color}cc 100%)`
                }}
                aria-hidden
              >
                {app.initial}
              </div>
              <Flex vertical style={{ minWidth: 0, lineHeight: 1.3 }} gap={2}>
                <Flex align="center" gap={8}>
                  <Text strong style={{ fontSize: 13 }} ellipsis>
                    {app.name}
                  </Text>
                  <Tag
                    color={HEALTH_COLORS[app.health]}
                    style={{
                      marginInlineEnd: 0,
                      fontSize: 10,
                      textTransform: 'capitalize'
                    }}
                  >
                    {app.health}
                  </Tag>
                </Flex>
                <Text type="secondary" style={{ fontSize: 11 }} ellipsis>
                  {app.users} users · {app.roles} roles · {app.category}
                </Text>
              </Flex>
            </Flex>

            <Flex align="center" gap={20}>
              <Flex
                vertical
                align="flex-end"
                style={{ width: 80, lineHeight: 1.2 }}
              >
                <Text className={styles.metricLabel}>
                  {t('workspaceOverview.appsHealth.sessions')}
                </Text>
                <Text className={styles.metricValue}>
                  {app.activeSessions.toLocaleString()}
                </Text>
              </Flex>
              <Flex
                vertical
                align="flex-end"
                style={{ width: 70, lineHeight: 1.2 }}
              >
                <Text className={styles.metricLabel}>
                  {t('workspaceOverview.appsHealth.errorRate')}
                </Text>
                <Text
                  className={styles.metricValue}
                  style={{
                    color:
                      app.errorRate > 1
                        ? 'var(--ant-color-error)'
                        : app.errorRate > 0.5
                          ? 'var(--ant-color-warning)'
                          : 'var(--ant-color-success)'
                  }}
                >
                  {app.errorRate}%
                </Text>
              </Flex>
              <Flex
                vertical
                align="flex-end"
                style={{ width: 64, lineHeight: 1.2 }}
              >
                <Text className={styles.metricLabel}>
                  {t('workspaceOverview.appsHealth.p95')}
                </Text>
                <Text className={styles.metricValue}>{app.p95}ms</Text>
              </Flex>
              <Sparkline data={app.spark} color={app.color} />
            </Flex>
          </Flex>
        ))}
      </Flex>
    </Card>
  )
}
